// New / edit event sheet — title, day(s), time/all-day, location, notes.
// Same component handles both flows. When `editing` is set, fields are
// pre-populated and Save calls onUpdate(id, patch). Otherwise Save calls
// onCreate(args).

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Calendar, type CalendarEvent, type CalendarCreateArgs, type CalendarUpdatePatch, type HouseholdMember } from "../../lib/rpc";
import {
  CAL_DAYS, allDayIso, daysFrom, fmtTime, halfHourOptions, isoAtHour,
  isoToHour, sameDay, weekStart,
} from "../../lib/calendar-utils";

export interface NewEventPrefill {
  day?: Date;
  hour?: number;
  /** When the user creates while a calendar filter is active, default
   *  the new event to that calendar. */
  calendarId?: string | null;
}

interface NewEventProps {
  open: boolean;
  editing: CalendarEvent | null;
  prefill: NewEventPrefill | null;
  weekAnchor: Date;
  calendars: Calendar[];
  onClose: () => void;
  onCreate: (args: CalendarCreateArgs) => Promise<unknown>;
  onUpdate: (id: string, patch: CalendarUpdatePatch) => Promise<unknown>;
}

interface DraftEvent {
  title: string;
  startDate: Date;     // anchor day (for timed events: also the date of start time)
  endDate: Date;       // for all-day multi-day events; same as startDate otherwise
  startHour: number;   // decimal 0–24
  endHour: number;     // decimal 0–24
  allDay: boolean;
  location: string;
  notes: string;
  calendarId: string | null;
  /** Minutes-before-start alarms the user picked (empty = no
   *  reminders). The Remind-me chip row toggles entries in this set. */
  reminders: number[];
  /** RFC 5545 RRULE for recurring events, or `null` for one-off. The
   *  picker translates preset chips ("Weekly", "Monthly", …) into a
   *  canonical string; the "Custom" preset reveals a raw input so
   *  power users can write whatever rrule they need (e.g. every other
   *  Friday). */
  rrule: string | null;
  /** Per-household-member tag. `null` = household-wide (default and
   *  current behavior); set to a user id to pin the event to that
   *  member — typically a kid, so it surfaces in their kid Calendar
   *  view. The dropdown is only useful in households with kids; if the
   *  caller doesn't have kid members, we just leave it null and hide
   *  the row. */
  forUserId: string | null;
}

// Recurrence presets — chips offered in the picker, each mapped to a
// canonical RRULE generated from the event's start date. "Custom" reveals
// a free-form input field for unusual cases (BYSETPOS, COUNT/UNTIL, etc.).
type RecurPreset = "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom";
const RECUR_PRESETS: { id: RecurPreset; label: string }[] = [
  { id: "none",    label: "Doesn't repeat" },
  { id: "daily",   label: "Daily" },
  { id: "weekly",  label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly",  label: "Yearly" },
  { id: "custom",  label: "Custom…" },
];

const RRULE_DAY_NAMES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function rruleForPreset(id: RecurPreset, dtstart: Date): string | null {
  if (id === "none" || id === "custom") return null;
  if (id === "daily")   return "FREQ=DAILY";
  if (id === "weekly")  return `FREQ=WEEKLY;BYDAY=${RRULE_DAY_NAMES[dtstart.getDay()]}`;
  if (id === "monthly") return `FREQ=MONTHLY;BYMONTHDAY=${dtstart.getDate()}`;
  if (id === "yearly")  return "FREQ=YEARLY";
  return null;
}

// Reverse map — given an existing rrule + dtstart, figure out which
// preset the user picked so the chip row paints with the right one on
// edit open. Anything that doesn't match a canonical preset falls into
// "custom" and the rrule shows verbatim in the input.
function presetForRrule(rrule: string | null, dtstart: Date): RecurPreset {
  if (!rrule) return "none";
  if (rrule === "FREQ=DAILY") return "daily";
  if (rrule === `FREQ=WEEKLY;BYDAY=${RRULE_DAY_NAMES[dtstart.getDay()]}`) return "weekly";
  if (rrule === `FREQ=MONTHLY;BYMONTHDAY=${dtstart.getDate()}`) return "monthly";
  if (rrule === "FREQ=YEARLY") return "yearly";
  return "custom";
}

// Preset offsets the chip row offers. Custom entry isn't in Phase 1 —
// the most common picks are the four below + the user can stack them.
const REMINDER_PRESETS: { mins: number; label: string }[] = [
  { mins: 0,    label: "At start" },
  { mins: 10,   label: "10 min" },
  { mins: 30,   label: "30 min" },
  { mins: 60,   label: "1 hour" },
  { mins: 1440, label: "1 day" },
];

function emptyDraft(prefill: NewEventPrefill | null, weekAnchor: Date): DraftEvent {
  const day = prefill?.day || new Date(weekAnchor);
  const hour = prefill?.hour ?? 9;
  return {
    title: "",
    startDate: day,
    endDate: day,
    startHour: hour,
    endHour: hour + 1,
    allDay: false,
    location: "",
    notes: "",
    calendarId: prefill?.calendarId ?? null,
    reminders: [],
    rrule: null,
    forUserId: null,
  };
}

function draftFromEvent(evt: CalendarEvent, reminders: number[] = []): DraftEvent {
  const start = new Date(evt.dtstart);
  const end = new Date(evt.dtend);
  // RFC 5545 all-day dtend is exclusive — display end is the day before.
  const endDisplay = evt.allDay ? (() => { const d = new Date(end); d.setDate(d.getDate() - 1); return d; })() : end;
  return {
    title: evt.title,
    startDate: start,
    endDate: endDisplay,
    startHour: evt.allDay ? 9 : isoToHour(evt.dtstart),
    endHour: evt.allDay ? 10 : isoToHour(evt.dtend),
    allDay: evt.allDay,
    location: evt.location || "",
    notes: evt.description || "",
    calendarId: evt.calendarId,
    reminders,
    rrule: evt.rrule || null,
    forUserId: evt.forUserId || null,
  };
}

export function NewEventModal({
  open, editing, prefill, weekAnchor, calendars, onClose, onCreate, onUpdate,
}: NewEventProps) {
  const [draft, setDraft] = useState<DraftEvent>(() =>
    editing ? draftFromEvent(editing) : emptyDraft(prefill, weekAnchor)
  );
  const [saving, setSaving] = useState(false);
  // Active-household members for the "For whom" dropdown (v52 per-kid
  // event tagging). Loaded lazily on modal open. Empty array hides the
  // row entirely so households without kids don't see clutter.
  const [members, setMembers] = useState<HouseholdMember[]>([]);

  // Reset the draft any time the modal (re)opens with a different
  // target, including swapping between "new" and "edit" mid-session.
  // For edit-mode we also fetch the event's existing reminders so the
  // chip row paints with the current selection.
  useEffect(() => {
    if (!open) return;
    if (!editing) {
      setDraft(emptyDraft(prefill, weekAnchor));
      return;
    }
    // Optimistic paint with empty reminders, then patch in the fetched
    // set when it arrives. Avoids a perceptible delay on edit open.
    setDraft(draftFromEvent(editing, []));
    let cancelled = false;
    api.calendarListReminders(editing.id).then((reminders) => {
      if (cancelled) return;
      setDraft((d) => ({ ...d, reminders }));
    }).catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Load the active household's members so we can offer "For whom" as
  // a dropdown. Only adults+kids in the active household are surfaced
  // — cross-household tagging isn't supported (and wouldn't make sense
  // since the visibility predicate filters by household anyway).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.householdsList()
      .then((r) => {
        const active = r.households.find((h) => h.id === r.activeHouseholdId);
        if (!active) return [];
        return api.householdsListMembers(active.id);
      })
      .then((m) => { if (!cancelled) setMembers(m || []); })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const wkStart = weekStart(draft.startDate);
  const week = daysFrom(wkStart, 7);
  const multiDay = !sameDay(draft.startDate, draft.endDate);

  const setField = <K extends keyof DraftEvent>(k: K, v: DraftEvent[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    if (!draft.title.trim() || saving) return;
    setSaving(true);
    try {
      if (draft.allDay) {
        // All-day dtend is exclusive (next day after the last
        // included day). The server stores date-only strings for these.
        const endExclusive = new Date(draft.endDate);
        endExclusive.setDate(endExclusive.getDate() + 1);
        const args = {
          title: draft.title.trim(),
          dtstart: allDayIso(draft.startDate),
          dtend: allDayIso(endExclusive),
          allDay: true,
          location: draft.location.trim() || undefined,
          description: draft.notes.trim() || undefined,
          calendarId: draft.calendarId,
          reminders: draft.reminders.slice().sort((a, b) => a - b),
          rrule: draft.rrule || (editing ? null : undefined),
          forUserId: draft.forUserId,
        };
        if (editing) await onUpdate(editing.id, args);
        else await onCreate(args);
      } else {
        // Timed event — same day for now (no cross-midnight UI yet;
        // user can edit raw if needed).
        const args = {
          title: draft.title.trim(),
          dtstart: isoAtHour(draft.startDate, draft.startHour),
          dtend: isoAtHour(draft.startDate, draft.endHour),
          allDay: false,
          location: draft.location.trim() || undefined,
          description: draft.notes.trim() || undefined,
          calendarId: draft.calendarId,
          reminders: draft.reminders.slice().sort((a, b) => a - b),
          // Send rrule whenever editing (so null clears recurrence) or
          // when set on a new event (so undefined doesn't clobber the
          // default `null` server-side). Same shape as reminders above.
          rrule: draft.rrule || (editing ? null : undefined),
          forUserId: draft.forUserId,
        };
        if (editing) await onUpdate(editing.id, args);
        else await onCreate(args);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cal-modal-bg" onClick={onClose}>
      <div className="cal-new-sheet" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="cal-new-handle" />
        <header className="cal-new-head">
          <div>
            <h3 className="cal-new-title">{editing ? "Edit event" : "New event"}</h3>
            <div className="cal-new-sub">{editing ? "Tweak it and save." : "Drop something on the calendar."}</div>
          </div>
          <button className="icon-btn" onClick={onClose} type="button">
            <Icon name="x" />
          </button>
        </header>

        <div className="cal-new-body">
          <div className="cal-new-row">
            <label>Title</label>
            <input
              className="input"
              placeholder="e.g. Coffee with Marcus"
              value={draft.title}
              onInput={(e) => setField("title", (e.currentTarget as HTMLInputElement).value)}
              autoFocus
            />
          </div>

          {calendars.length > 0 && (
            <div className="cal-new-row">
              <label>Calendar</label>
              <select
                className="input"
                value={draft.calendarId || ""}
                onChange={(e) => {
                  const v = (e.currentTarget as HTMLSelectElement).value;
                  setField("calendarId", v ? v : null);
                }}
              >
                <option value="">Personal</option>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="cal-new-row">
            <label>Day{multiDay ? " — start" : ""}</label>
            <div className="cal-new-days">
              {week.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  data-active={sameDay(d, draft.startDate)}
                  onClick={() => {
                    setField("startDate", d);
                    // Keep endDate sane: never before startDate.
                    if (draft.endDate < d) setField("endDate", d);
                  }}
                >
                  <span className="am-day-name">{CAL_DAYS[i]}</span>
                  <span className="am-day-date">{d.getDate()}</span>
                </button>
              ))}
            </div>

            {draft.allDay && (
              <>
                <label className="cal-new-inline-check">
                  <span className="cbx" data-checked={multiDay}
                    onClick={() => {
                      if (multiDay) setField("endDate", draft.startDate);
                      else {
                        const next = new Date(draft.startDate);
                        next.setDate(next.getDate() + 1);
                        setField("endDate", next);
                      }
                    }}>
                    <Icon name="check" size={12} />
                  </span>
                  Multi-day
                </label>
                {multiDay && (
                  <div className="cal-new-days" style={{ marginTop: 6 }}>
                    {week.map((d, i) => {
                      const disabled = d < draft.startDate;
                      return (
                        <button
                          key={i}
                          type="button"
                          data-active={sameDay(d, draft.endDate)}
                          disabled={disabled}
                          onClick={() => { if (!disabled) setField("endDate", d); }}
                        >
                          <span className="am-day-name">{CAL_DAYS[i]}</span>
                          <span className="am-day-date">{d.getDate()}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="cal-new-row">
            <label>Time</label>
            <label className="cal-new-inline-check">
              <span className="cbx" data-checked={draft.allDay}
                onClick={() => setField("allDay", !draft.allDay)}>
                <Icon name="check" size={12} />
              </span>
              All day
            </label>
            {!draft.allDay && (
              <div className="cal-new-grid">
                <div>
                  <label className="cal-new-mini-label">starts</label>
                  <select className="input" value={draft.startHour}
                    onChange={(e) => {
                      const v = parseFloat((e.currentTarget as HTMLSelectElement).value);
                      setField("startHour", v);
                      if (draft.endHour <= v) setField("endHour", Math.min(24, v + 1));
                    }}>
                    {halfHourOptions().map((h) => (
                      <option key={h} value={h}>{fmtTime(h)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="cal-new-mini-label">ends</label>
                  <select className="input" value={draft.endHour}
                    onChange={(e) => setField("endHour", parseFloat((e.currentTarget as HTMLSelectElement).value))}>
                    {halfHourOptions().filter((h) => h > draft.startHour).map((h) => (
                      <option key={h} value={h}>{fmtTime(h)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="cal-new-row">
            <label>Location (optional)</label>
            <input
              className="input"
              placeholder="Address, link, or 'our place'"
              value={draft.location}
              onInput={(e) => setField("location", (e.currentTarget as HTMLInputElement).value)}
            />
          </div>

          {members.length > 1 && (
            <div className="cal-new-row">
              <label>For whom</label>
              <select
                className="input"
                value={draft.forUserId || ""}
                onChange={(e) => setField("forUserId", (e.currentTarget as HTMLSelectElement).value || null)}
              >
                <option value="">Everyone in the household</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.userId}{m.role === "kid" ? " (kid)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="cal-new-row">
            <label>Remind me</label>
            <div className="cal-new-reminders">
              {REMINDER_PRESETS.map((p) => {
                const active = draft.reminders.includes(p.mins);
                return (
                  <button
                    key={p.mins}
                    type="button"
                    className="cal-reminder-chip"
                    data-active={active}
                    onClick={() => {
                      // Toggle this offset. Multiple can be active —
                      // server stores the full set.
                      setField("reminders",
                        active
                          ? draft.reminders.filter((m) => m !== p.mins)
                          : [...draft.reminders, p.mins]);
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
              {draft.reminders.length === 0 && (
                <span className="cal-reminder-hint">None</span>
              )}
            </div>
          </div>

          <div className="cal-new-row">
            <label>Repeats</label>
            <div className="cal-recur-chips">
              {RECUR_PRESETS.map((p) => {
                const currentPreset = presetForRrule(draft.rrule, draft.startDate);
                const active = currentPreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="cal-reminder-chip"
                    data-active={active}
                    onClick={() => {
                      if (p.id === "custom") {
                        // Switching INTO custom: seed with current rrule
                        // (so the user can tweak what's there) or empty.
                        if (currentPreset !== "custom") {
                          setField("rrule", draft.rrule || "");
                        }
                      } else {
                        setField("rrule", rruleForPreset(p.id, draft.startDate));
                      }
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {presetForRrule(draft.rrule, draft.startDate) === "custom" && (
              <input
                className="input"
                style={{ marginTop: 8, fontFamily: "var(--f-mono)", fontSize: 12.5 }}
                placeholder="FREQ=WEEKLY;BYDAY=TU,TH"
                value={draft.rrule || ""}
                onInput={(e) => setField("rrule", (e.currentTarget as HTMLInputElement).value || null)}
              />
            )}
          </div>

          <div className="cal-new-row">
            <label>Notes (optional)</label>
            <textarea
              className="input cal-new-textarea"
              rows={2}
              placeholder="Anything you'd want to see when this lands…"
              value={draft.notes}
              onInput={(e) => setField("notes", (e.currentTarget as HTMLTextAreaElement).value)}
            />
          </div>
        </div>

        <footer className="cal-new-foot">
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={!draft.title.trim() || saving}
            type="button"
          >
            <Icon name="check" size={14} /> {editing ? "Save changes" : "Save event"}
          </button>
        </footer>
      </div>
    </div>
  );
}
